// The order the dashboard lists them in, which is also the order they are saved in.
export const contactTypes = [
  'CUSTOMER_SERVICE',
  'LOGISTICS',
  'INVOICING',
  'TECHNICAL_SUPPORT',
  'COMMERCIAL'
] as const

export type ContactType = (typeof contactTypes)[number]

export interface EcommerceContact {
  type: ContactType
  name: string
  email: string
  phone: string
}

// Stored as one contact per type, so name and phone repeat on each of them.
export interface PointOfContact {
  types: ContactType[]
  name: string
  phone: string
}

// Mirrors settings-api's onboarding requiredContactTypes.
export const requiredOnboardingContactTypes: ContactType[] = [
  'CUSTOMER_SERVICE',
  'COMMERCIAL'
]

export function missingOnboardingContactTypes(
  contacts: EcommerceContact[]
): ContactType[] {
  return requiredOnboardingContactTypes.filter(
    (type) => !contacts.some((contact) => contact.type === type)
  )
}

// Mirror settings-api's limits, so a save is not refused after the user is already stored.
export const MAX_CONTACT_NAME_LENGTH = 200
export const MAX_CONTACT_PHONE_LENGTH = 50
export const MAX_CONTACTS_PER_TYPE = 20

// Same rule as settings-api: "maria/laura@x.com" and "x@y.com/es" parse but bounce.
const DELIVERABLE_EMAIL =
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/

export function isDeliverableEmail(email: string) {
  return email.length <= 254 && DELIVERABLE_EMAIL.test(email)
}

export function pointOfContactFor(
  contacts: EcommerceContact[],
  email: string
): PointOfContact {
  const own = contacts.filter((contact) => sameEmail(contact.email, email))
  return {
    types: contactTypes.filter((type) =>
      own.some((contact) => contact.type === type)
    ),
    name: own.find((contact) => contact.name)?.name ?? '',
    phone: own.find((contact) => contact.phone)?.phone ?? ''
  }
}

// Saving replaces every contact of the ecommerce, so other emails' contacts are carried over.
export function withPointOfContact(
  contacts: EcommerceContact[],
  email: string,
  pointOfContact: PointOfContact
): EcommerceContact[] {
  const others = contacts.filter((contact) => !sameEmail(contact.email, email))
  const own = pointOfContact.types.map((type) => ({
    type,
    email,
    name: pointOfContact.name,
    phone: pointOfContact.phone
  }))
  return contactTypes.flatMap((type) =>
    [...others, ...own].filter((contact) => contact.type === type)
  )
}

// Types that cannot take one more email: the email's own contacts are replaced on save, so they do not count.
export function fullContactTypes(
  contacts: EcommerceContact[],
  email: string
): ContactType[] {
  const others = contacts.filter((contact) => !sameEmail(contact.email, email))
  return contactTypes.filter(
    (type) =>
      others.filter((contact) => contact.type === type).length >=
      MAX_CONTACTS_PER_TYPE
  )
}

export function mergePointOfContact(
  current: PointOfContact,
  added: PointOfContact
): PointOfContact {
  return {
    types: contactTypes.filter(
      (type) => current.types.includes(type) || added.types.includes(type)
    ),
    name: added.name || current.name,
    phone: added.phone || current.phone
  }
}

export function samePointOfContact(a: PointOfContact, b: PointOfContact) {
  return (
    a.name === b.name &&
    a.phone === b.phone &&
    a.types.length === b.types.length &&
    a.types.every((type) => b.types.includes(type))
  )
}

function sameEmail(a: string, b: string) {
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}
